import {
    DataProvider,
    GetListParams,
    GetListResult,
    GetManyParams,
    GetManyResult,
    GetOneParams,
    GetOneResult,
} from 'react-admin';
import { read_cookie } from 'sfcookies';
import { apiUrl, UseUrlManagerCleanUrl } from '../components/utils/url-manager';
import { addSortToUrl, processFilters } from './provider/defaults';
import { NokProcessingFailure } from '../types/entities';

/**
 * Read-only provider for the Nok scoring failures list.
 *
 * Modelled on salesConversationDataProvider rather than on the 400-line template the other
 * resources copy, for both of its reasons. A failure row is written by HQ-API's failure paths and
 * cleared by a rescore, so `create`, `update` and `delete` would be dead code here and the API
 * resource exposes no write operations to reach anyway. More to the point, those copies swallow a
 * non-2xx and hand react-admin an empty result (HQ-06), and on this screen an empty result means
 * "nothing failed" — the single most misleading thing it could say.
 */

const headers = (): Headers => new Headers({
    'Content-Type': 'application/json',
    'token': read_cookie('auth'),
    'hq': 'hq',
});

/**
 * `hydra:member` rows carry an `@id` IRI; react-admin wants a plain `id`, which the API already
 * serialises alongside it.
 */
const fetchCollection = async (url: string): Promise<{ data: NokProcessingFailure[], total: number }> => {
    const request: Request = new Request(UseUrlManagerCleanUrl({ url }), { method: 'GET', headers: headers() });
    const response: Response = await fetch(request);
    if (!response.ok) {
        throw new Error('Could not load the processing failures');
    }
    const body: any = await response.json();

    return {
        data: body?.['hydra:member'] ?? body?.member ?? [],
        total: body?.['hydra:totalItems'] ?? body?.totalItems ?? 0,
    };
};

const nokProcessingFailureDataProvider: DataProvider = {
    getList: async (resource: string, params: GetListParams): Promise<GetListResult> => {
        let url: string = apiUrl + resource;
        if (params.pagination) {
            url += `?page=${ params.pagination.page }&itemsPerPage=${ params.pagination.perPage }`;
        }
        url = processFilters(url, params);
        url = addSortToUrl(url, params);

        return fetchCollection(url);
    },

    getOne: async (resource: string, params: GetOneParams): Promise<GetOneResult> => {
        const url: string = UseUrlManagerCleanUrl({ url: `${ apiUrl }${ resource }/${ params.id }` });
        const response: Response = await fetch(new Request(url, { method: 'GET', headers: headers() }));
        if (!response.ok) {
            throw new Error('Could not load the processing failure');
        }

        return { data: await response.json() };
    },

    getMany: async (resource: string, params: GetManyParams): Promise<GetManyResult> => {
        if (params.ids.length === 0) {
            return { data: [] };
        }
        const query: string = params.ids.map((id): string => `id[]=${ String(id) }`).join('&');
        const { data } = await fetchCollection(`${ apiUrl }${ resource }?${ query }`);

        return { data };
    },

    // Rows are written by HQ-API and cleared by a rescore. A hand-edited attempt count or status
    // would put the retry logic and the record out of step with no way to tell which was right.
    getManyReference: (): Promise<never> => Promise.reject(new Error('Not supported')),
    create: (): Promise<never> => Promise.reject(new Error('Failures are recorded by HQ-API, not created')),
    update: (): Promise<never> => Promise.reject(new Error('Failures are cleared by a rescore, not edited')),
    updateMany: (): Promise<never> => Promise.reject(new Error('Not supported')),
    delete: (): Promise<never> => Promise.reject(new Error('Not supported')),
    deleteMany: (): Promise<never> => Promise.reject(new Error('Not supported')),
};

export default nokProcessingFailureDataProvider;
